/**
 * Plain-language labels + tooltips for every toggle and product shown in the UI.
 * The audience is non-technical (marketing / sales / product), so keep copy
 * friendly and jargon-free. Every switch/card reads its text from here.
 */

export interface Labelled {
  label: string;
  tooltip: string;
}

// kim-ai feature guards (accounts.flag JSONB)
export const FEATURE_GUARD_LABELS: Record<string, Labelled> = {
  shadow_mode_enabled: {
    label: 'Shadow Mode',
    tooltip:
      'AI drafts replies for your team to review — nothing is sent to customers automatically.'
  },
  v3: {
    label: 'New AI Engine (V3)',
    tooltip:
      "Kim's latest AI engine. Turning this on switches the account off the older engines."
  },
  v3_shadow: {
    label: 'New Engine — Review Only',
    tooltip:
      "Runs the new AI engine in draft-only mode. Can't be on at the same time as the New AI Engine."
  },
  workflow_v2: {
    label: 'Workflows (V2)',
    tooltip:
      "The older workflow system. Can't run at the same time as the New AI Engine."
  },
  ai_settings_enabled: {
    label: 'AI Settings Access',
    tooltip: 'Lets this account see and edit their own AI settings.'
  },
  social_agent_enabled: {
    label: 'Social Media Agent',
    tooltip:
      'Lets the agent reply to and moderate Facebook and Instagram comments, using the rules set up for this account. Off means it never acts.'
  },
  intent_automation_kill_switch: {
    label: 'Emergency Stop',
    tooltip: 'Immediately pauses all automatic AI replies for this account.'
  },
  automation_all_gates_off: {
    label: 'All gates off',
    tooltip:
      'Full automation with every safety check removed: every reply the AI writes is sent to the customer, whatever its quality score. Demo, Pilot and Kim Internal accounts only. Emergency Stop and Shadow Mode still apply.'
  }
};

/**
 * Account statuses that may turn on All gates off. Mirrors machine's
 * ALL_GATES_OFF_ACCOUNT_STATUSES, which refuses the write for any other status.
 */
export const ALL_GATES_OFF_ACCOUNT_STATUSES = ['Demo', 'Pilot', 'Kim Internal'];

export const FEATURE_GUARD_ORDER = [
  'v3',
  'v3_shadow',
  'workflow_v2',
  'shadow_mode_enabled',
  'ai_settings_enabled',
  'social_agent_enabled',
  'intent_automation_kill_switch',
  'automation_all_gates_off'
];

// candy AI-insights flags (accounts.feature_flags bitmask)
export const AI_INSIGHT_LABELS: Record<string, Labelled> = {
  insight_generation: {
    label: 'AI Insights',
    tooltip: 'Generates insight summaries from customer conversations.'
  },
  ai_suggestion_generation: {
    label: 'AI Reply Suggestions',
    tooltip: 'Suggests replies to agents based on past conversations.'
  },
  ai_actions: {
    label: 'AI Actions',
    tooltip: 'Lets the AI take actions (like tagging or routing) on conversations.'
  },
  conversation_classification: {
    label: 'Auto-Categorize Conversations',
    tooltip: 'Automatically sorts conversations into categories.'
  }
};

export const AI_INSIGHT_ORDER = [
  'insight_generation',
  'ai_suggestion_generation',
  'ai_actions',
  'conversation_classification'
];

// Products
export const PRODUCT_LABELS: Record<string, Labelled> = {
  helpdesk: {
    label: 'Helpdesk',
    tooltip: 'The shared inbox and ticketing workspace.'
  },
  kim_ai: {
    label: 'Kim AI',
    tooltip: 'The AI assistant that drafts and sends replies.'
  },
  pulse: {
    label: 'Pulse',
    tooltip: 'Conversation analytics and insight reporting.'
  }
};

export const PRODUCT_ORDER = ['helpdesk', 'kim_ai', 'pulse'];

// Account status options (must match backend AccountStatus enum)
export const ACCOUNT_STATUSES = [
  'Active',
  'Inactive',
  'Demo',
  'Pilot',
  'Kim Internal',
  'Partnership',
  'Junk'
];

// Account source tags (Postgres / choco Mongo merge)
export const SOURCE_LABELS: Record<
  string,
  { label: string; tooltip: string }
> = {
  both: {
    label: 'In both systems',
    tooltip: 'This account exists in both the main system and the legacy system.'
  },
  postgres: {
    label: 'Main system',
    tooltip: 'This account exists only in the main system.'
  },
  mongo: {
    label: 'Legacy only',
    tooltip: 'This account exists only in the legacy (Pulse) system.'
  }
};
